/**
 * Comparative advantage for two producers and two goods, with straight-line production
 * possibilities frontiers.
 *
 * A producer's frontier is fixed by the most of each good it could make in one period
 * with all its resources on that good. With x the most of good 1 and y the most of good
 * 2, the opportunity cost of one unit of good 1 is y ÷ x units of good 2, and of one
 * unit of good 2 it is x ÷ y units of good 1. Hours per unit convert to output by
 * taking the reciprocal, which makes the opportunity cost of good 1 the ratio of hours,
 * t₁ ÷ t₂.
 *
 * The producer with the lower opportunity cost of a good has the comparative advantage
 * in it; trade is mutually beneficial when the price of good 1 (in units of good 2) lies
 * strictly between the two producers' opportunity costs of good 1.
 */

export interface Producer {
  /** The most of good 1 made in a period with every resource on good 1. */
  good1: number;
  /** The most of good 2. */
  good2: number;
}

/** Hours per unit to a producer's maximum output in one hour. */
export function fromHours(hours1: number, hours2: number): Producer {
  return { good1: 1 / hours1, good2: 1 / hours2 };
}

export type Who = 0 | 1;
export type Winner = Who | "neither";

/** Opportunity cost of one unit of good 1, in units of good 2. */
export function costOfGood1(p: Producer): number {
  return p.good2 / p.good1;
}

/** Opportunity cost of one unit of good 2, in units of good 1. */
export function costOfGood2(p: Producer): number {
  return p.good1 / p.good2;
}

const TOLERANCE = 1e-9;

function lowerIs(a: number, b: number): Winner {
  if (Math.abs(a - b) <= TOLERANCE * Math.max(1, Math.abs(a), Math.abs(b))) {
    return "neither";
  }
  return a < b ? 0 : 1;
}

function higherIs(a: number, b: number): Winner {
  const lower = lowerIs(a, b);
  if (lower === "neither") {
    return lower;
  }
  return lower === 0 ? 1 : 0;
}

export interface Analysis {
  costGood1: [number, number];
  costGood2: [number, number];
  absoluteGood1: Winner;
  absoluteGood2: Winner;
  comparativeGood1: Winner;
  comparativeGood2: Winner;
  /** True when each producer has the comparative advantage in a different good. */
  gainsFromTrade: boolean;
  /** Price of good 1 in good 2 at which only the good-1 specialist gains … */
  rangeLow: number | null;
  /** … and only the other gains; trade is mutually beneficial strictly between. */
  rangeHigh: number | null;
  /** Extra units of good 2 per unit of good 1 traded, shared out by the terms. */
  gainPerUnit: number | null;
}

/** Absolute and comparative advantage for producers `a` (0) and `b` (1). */
export function analyse(a: Producer, b: Producer): Analysis {
  const costGood1: [number, number] = [costOfGood1(a), costOfGood1(b)];
  const costGood2: [number, number] = [costOfGood2(a), costOfGood2(b)];
  const comparativeGood1 = lowerIs(costGood1[0], costGood1[1]);
  const comparativeGood2 = lowerIs(costGood2[0], costGood2[1]);
  const gainsFromTrade = comparativeGood1 !== "neither";
  let rangeLow: number | null = null;
  let rangeHigh: number | null = null;
  if (comparativeGood1 !== "neither") {
    rangeLow = costGood1[comparativeGood1];
    rangeHigh = costGood1[comparativeGood1 === 0 ? 1 : 0];
  }
  return {
    costGood1,
    costGood2,
    absoluteGood1: higherIs(a.good1, b.good1),
    absoluteGood2: higherIs(a.good2, b.good2),
    comparativeGood1,
    comparativeGood2,
    gainsFromTrade,
    rangeLow,
    rangeHigh,
    gainPerUnit:
      rangeLow !== null && rangeHigh !== null ? rangeHigh - rangeLow : null,
  };
}

export interface TradeGains {
  /** Good 2 gained per unit of good 1 traded, by the good-1 specialist: p − low. */
  specialist1: number;
  /** Good 2 saved per unit of good 1 traded, by the good-2 specialist: high − p. */
  specialist2: number;
  /** Whether p lies strictly inside the range, so both gain. */
  mutual: boolean;
}

export function gainsAt(analysis: Analysis, price: number): TradeGains | null {
  const { rangeLow: low, rangeHigh: high } = analysis;
  if (low === null || high === null) {
    return null;
  }
  return {
    specialist1: price - low,
    specialist2: high - price,
    mutual: price > low && price < high,
  };
}

/**
 * The consumption line a producer faces when it specializes and trades at `price`
 * (units of good 2 per unit of good 1): the end points on the axes.
 */
export function tradeLine(
  producer: Producer,
  specializesInGood1: boolean,
  price: number
): [[number, number], [number, number]] {
  if (specializesInGood1) {
    return [
      [producer.good1, 0],
      [0, producer.good1 * price],
    ];
  }
  return [
    [0, producer.good2],
    [producer.good2 / price, 0],
  ];
}
